import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { Typography } from './Typography';
import { Divider } from './Divider';

export type TableHeaderProps = {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  tabs?: React.ReactNode;
  disableTitleTypography?: boolean;
  bulkSelected?: number;
  bulkActions?: React.ReactNode;
  formatBulkSelectionLabel?: (count?: number) => React.ReactNode;
  minHeight?: number | string;
  sx?: SxProps<Theme>;
};

export const tableActionsContainerTestId = 'table-actions-container';
const DEFAULT_TABLE_HEADER_MIN_HEIGHT = '70px';

export const TableHeader: React.FC<TableHeaderProps> = ({
  title,
  subtitle,
  actions,
  tabs,
  disableTitleTypography = false,
  bulkSelected,
  bulkActions,
  formatBulkSelectionLabel,
  minHeight,
  sx,
}) => {
  const isBulkSelected = bulkSelected && bulkSelected > 0;

  const renderTitle = () => {
    if (title) {
      return (
        <Box maxWidth={400} py={3} mr={4}>
          {disableTitleTypography ? (
            title
          ) : (
            <Typography variant='h6' component='h2' gutterBottom={!!subtitle}>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant='body2' color='textSecondary'>
              {subtitle}
            </Typography>
          )}
        </Box>
      );
    }

    return null;
  };

  const renderBulkHeader = () => (
    <>
      <Box maxWidth={400} py={3} mr={4}>
        <Typography component='div'>
          {formatBulkSelectionLabel
            ? formatBulkSelectionLabel(bulkSelected)
            : `${bulkSelected} selected`}
        </Typography>
      </Box>
      {bulkActions && (
        <Box display='flex' alignItems='center' justifyContent='flex-end' flex='1' minWidth={200}>
          {bulkActions}
        </Box>
      )}
    </>
  );

  const renderHeader = () => (
    <>
      {renderTitle()}
      {tabs && (
        <Box alignSelf='flex-end' mr={4}>
          {tabs}
        </Box>
      )}
      {actions && (
        <Box
          display='flex'
          alignItems='center'
          justifyContent='flex-end'
          flex='1'
          minWidth={200}
          data-testid={tableActionsContainerTestId}
        >
          {actions}
        </Box>
      )}
    </>
  );

  return (
    <Box sx={sx}>
      <Box
        display='flex'
        alignItems='center'
        px={3}
        minHeight={minHeight ?? DEFAULT_TABLE_HEADER_MIN_HEIGHT}
        bgcolor={({ palette }) =>
          isBulkSelected
            ? alpha(palette.primary.main, palette.action.activatedOpacity)
            : palette.common.white
        }
      >
        {isBulkSelected ? renderBulkHeader() : renderHeader()}
      </Box>
      <Divider />
    </Box>
  );
};
